<template>
  <jd-config-provider>
    <div
      ref="rootRef"
      style="display: contents"
      :data-track-root-id="trackRootId"
    >
      <slot />
    </div>
  </jd-config-provider>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
import JdConfigProvider from '../../config-provider'
import { bubble, trackAnalyse } from './tools'

defineOptions({ name: 'JdTrackMonitor' })

const props = defineProps<{
  onTrack?: (trackData: any) => void
}>()

const rootRef = ref<HTMLElement | null>(null)
const trackRootId = `jd-track-monitor-${Math.random().toString(36).slice(2, 10)}`

provide('jdTrackMonitorRootId', trackRootId)

const buildTrack = (source: Record<string, any>) => {
  const get = (dashKey: string, camelKey: string) =>
    source[dashKey] ?? source[camelKey] ?? ''

  const eventLabel = get('data-event-label', 'eventLabel')

  return {
    path: eventLabel,
    eventLabel,
    eventContent: get('data-event-content', 'eventContent'),
    componentName: get('data-component-name', 'componentName'),
    eventName: get('data-event-name', 'eventName'),
  }
}

const syncTrackHandler = () => {
  const el = rootRef.value
  if (!el) return

  if (props.onTrack) {
    el.$jdTrack = props.onTrack
  } else {
    delete el.$jdTrack
  }
}

const trackDuration = (e: Event) => {
  const evt = e as CustomEvent<any>

  if (evt.detail && typeof evt.detail === 'object') {
    props.onTrack?.(buildTrack(evt.detail))
    return
  }

  if (evt.target instanceof HTMLElement) {
    props.onTrack?.(buildTrack(evt.target.dataset as any))
  }
}

const getClosestTrackRoot = (target: Element | null) => {
  return target?.closest('[data-track-root-id]') as HTMLElement | null
}

const isTrackTargetInScope = (target: Element | null) => {
  if (!target || !rootRef.value) return false

  const closestTrackRoot = getClosestTrackRoot(target)
  if (closestTrackRoot?.dataset.trackRootId) {
    return closestTrackRoot.dataset.trackRootId === trackRootId
  }

  return rootRef.value.contains(target)
}

const trackEvent = (e: MouseEvent) => {
  try {
    const evt = e as any
    if (evt.$jdTrack || !(evt.target instanceof Element)) return

    let track = props.onTrack
    if (rootRef.value) {
      for (const node of bubble(rootRef.value)) {
        if (node.$jdTrack) {
          track = node.$jdTrack
        }
      }
    }

    const trackData = trackAnalyse({
      target: evt.target as any,
      currentTarget: rootRef.value ?? (evt.target as Element).ownerDocument.body,
      detail: evt.detail,
    })
    if (track && trackData) {
      track(trackData)
      evt.$jdTrack = true
    }
  } catch (error) {
    console.error('TrackMonitor trackEvent error:', error)
  }
}

const onClick = (e: MouseEvent) => {
  const analyse = () => {
    trackEvent(e)
  }

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      requestAnimationFrame(analyse)
    })
  } else {
    setTimeout(analyse, 0)
  }
}

const onDocumentClick = (e: MouseEvent) => {
  if (!(e.target instanceof Element)) return
  if (!isTrackTargetInScope(e.target)) return
  onClick(e)
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('customTrack', trackDuration)
  syncTrackHandler()
})

watch(() => props.onTrack, syncTrackHandler)

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
  document.removeEventListener('customTrack', trackDuration)
  if (rootRef.value) {
    delete rootRef.value.$jdTrack
  }
})
</script>
